import { useEffect, useRef, useState } from "react";
import { cardStyle, ctaBtnStyle, inputStyle } from "../components/styles";
import { checkAuth, login } from "../auth";
import { useIsMobile } from "../lib/useIsMobile";

interface LoginProps {
  onSuccess: () => void;
}

export function Login({ onSuccess }: LoginProps): React.JSX.Element {
  const isMobile = useIsMobile();
  const [password, setPassword] = useState("");
  // Whether the code field is shown. Learned up front from /api/auth/me so the
  // operator sees password + code TOGETHER; the server's mfaRequired response
  // is a fallback that flips it on if enrollment changed since the page loaded.
  const [mfaRequired, setMfaRequired] = useState(false);
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryAt, setRetryAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    // Ask up front whether a code is required so both fields render together.
    checkAuth()
      .then((s) => setMfaRequired(s.mfaRequired === true))
      .catch(() => { /* offline — the login POST will still signal mfaRequired */ });
  }, []);

  useEffect(() => {
    if (retryAt === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [retryAt]);

  const lockedSecondsLeft = retryAt !== null ? Math.max(0, Math.ceil((retryAt - now) / 1000)) : 0;
  const locked = lockedSecondsLeft > 0;
  const otpComplete = otp.trim().length === 6;

  const submit = async (): Promise<void> => {
    if (!password || busy || locked) return;
    if (mfaRequired && !otpComplete) return;
    setBusy(true);
    setError(null);
    const result = await login(password, mfaRequired ? otp.trim() : undefined);
    setBusy(false);
    if (result.ok) {
      onSuccess();
      return;
    }
    // Reveal the code field if the server says it's needed (covers the case
    // where MFA was enabled after this page loaded, or the initial /me failed).
    if (result.mfaRequired && !mfaRequired) {
      setMfaRequired(true);
      setError(null); // first prompt for the code isn't an error
    } else {
      setError(result.error);
      // Wrong password → clear it; a wrong code keeps the password so the
      // operator only retypes the 6 digits.
      if (!result.mfaRequired) setPassword("");
      setOtp("");
    }
    if (result.retryAfterSec) {
      setRetryAt(Date.now() + result.retryAfterSec * 1000);
      setNow(Date.now());
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        position: "relative",
        zIndex: 1,
      }}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        style={{
          ...cardStyle,
          width: "100%",
          maxWidth: "min(420px, calc(100vw - 32px))",
          padding: isMobile ? "28px 18px 22px" : "36px 32px 32px",
          boxShadow: "0 24px 60px rgba(0,0,0,.55), 0 2px 0 rgba(255,255,255,.04) inset",
          animation: "ql-pop-in .25s var(--ql-ease)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            marginBottom: 28,
          }}
        >
          <div
            aria-hidden
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "linear-gradient(135deg,#FFD500,#FFA500)",
              boxShadow: "0 8px 24px rgba(255,213,0,.35), 0 0 32px rgba(255,213,0,.18)",
              marginBottom: 18,
            }}
          >
            <span
              style={{
                fontFamily: "Lalezar, var(--ql-font-sans)",
                fontSize: 30,
                fontWeight: 900,
                color: "#000",
                lineHeight: 1,
                letterSpacing: "-0.04em",
              }}
            >
              Q
            </span>
          </div>
          <div
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: "var(--ql-yellow)",
              textTransform: "uppercase",
              letterSpacing: ".18em",
              marginBottom: 4,
            }}
          >
            Quotologies
          </div>
          <div
            style={{
              fontSize: 22,
              fontWeight: 700,
              color: "var(--ql-fg)",
              letterSpacing: "-0.01em",
            }}
          >
            AI Editor
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--ql-fg-muted)",
              marginTop: 6,
            }}
          >
            {mfaRequired
              ? "Enter the operator password and your authenticator code."
              : "Enter the operator password to continue."}
          </div>
        </div>

        <label
          htmlFor="ql-login-password"
          style={{
            display: "block",
            fontSize: 12,
            fontWeight: 600,
            color: "var(--ql-fg-muted)",
            textTransform: "uppercase",
            letterSpacing: ".06em",
            marginBottom: 8,
          }}
        >
          Password
        </label>
        <input
          id="ql-login-password"
          ref={inputRef}
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={busy || locked}
          placeholder="••••••••"
          style={{
            ...inputStyle,
            fontSize: 15,
            letterSpacing: "0.08em",
            opacity: locked ? 0.55 : 1,
          }}
        />

        {mfaRequired && (
          <>
            <label
              htmlFor="ql-login-otp"
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: "var(--ql-fg-muted)",
                textTransform: "uppercase",
                letterSpacing: ".06em",
                margin: "16px 0 8px",
              }}
            >
              Authentication code
            </label>
            <input
              id="ql-login-otp"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={otp}
              // Strip non-digits then cap at 6 — order matters for pasted codes
              // that contain a space/hyphen.
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={onKeyDown}
              disabled={busy || locked}
              placeholder="123456"
              style={{
                ...inputStyle,
                fontSize: 18,
                letterSpacing: "0.35em",
                textAlign: "center",
                fontFamily: "var(--ql-font-mono)",
                opacity: locked ? 0.55 : 1,
              }}
            />
            <div style={{ fontSize: 11, color: "var(--ql-fg-subtle)", marginTop: 8, lineHeight: 1.5 }}>
              Enter the 6-digit code from your authenticator app.
            </div>
          </>
        )}

        {error && (
          <div
            role="alert"
            style={{
              marginTop: 14,
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid rgba(248,113,113,.30)",
              background: "rgba(239,68,68,.08)",
              color: "#FCA5A5",
              fontSize: 13,
              lineHeight: 1.45,
            }}
          >
            {locked
              ? `Too many attempts. Try again in ${lockedSecondsLeft}s.`
              : error}
          </div>
        )}

        <button
          type="submit"
          disabled={busy || locked || !password || (mfaRequired && !otpComplete)}
          style={{
            ...ctaBtnStyle(!busy && !locked && password.length > 0 && (!mfaRequired || otpComplete)),
            width: "100%",
            justifyContent: "center",
            marginTop: 22,
          }}
        >
          {busy ? "Signing in…" : locked ? `Locked (${lockedSecondsLeft}s)` : "Sign in"}
        </button>

        <div
          style={{
            marginTop: 22,
            paddingTop: 18,
            borderTop: "1px solid rgba(255,255,255,.05)",
            fontSize: 11,
            color: "var(--ql-fg-subtle)",
            textAlign: "center",
            lineHeight: 1.55,
          }}
        >
          Single-operator gate · session lasts 30 days.
        </div>
      </form>
    </div>
  );
}
